/* eslint-disable react/no-unescaped-entities */
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import LangToggle from "@/components/LangToggle";
import { useT } from "@/i18n";
import useSEO from "@/hooks/useSEO";

/**
 * Privacy Policy page — public, no auth required.
 *
 * Available in 6 languages (EN, TR, ES, FR, DE, IT) — the same set the rest
 * of the app supports. Fallback is English.
 */
export default function Privacy() {
  const { lang } = useT();
  useSEO({
    title: "Privacy Policy — Lumioos",
    description: "How Lumioos collects, stores and uses your data. GDPR rights, third-party processors (Stripe, OpenAI, Anthropic, Google), data retention, contact for deletion requests.",
    canonical: "https://mylumioos.com/privacy",
  });
  const labels = {
    en: { eyebrow: "Legal", title: "Privacy Policy", updated: "Last updated", terms: "Terms of Service", home: "Home" },
    tr: { eyebrow: "Yasal", title: "Gizlilik Politikası", updated: "Son güncelleme", terms: "Kullanım Koşulları", home: "Ana sayfa" },
    es: { eyebrow: "Legal", title: "Política de Privacidad", updated: "Última actualización", terms: "Términos del Servicio", home: "Inicio" },
    fr: { eyebrow: "Mentions légales", title: "Politique de Confidentialité", updated: "Dernière mise à jour", terms: "Conditions d'utilisation", home: "Accueil" },
    de: { eyebrow: "Rechtliches", title: "Datenschutzerklärung", updated: "Zuletzt aktualisiert", terms: "Nutzungsbedingungen", home: "Startseite" },
    it: { eyebrow: "Legale", title: "Informativa sulla Privacy", updated: "Ultimo aggiornamento", terms: "Termini di Servizio", home: "Home" },
  };
  const L = labels[lang] || labels.en;
  const Body = PRIVACY_BODIES[lang] || PRIVACY_BODIES.en;
  return (
    <div className="min-h-screen bg-white" data-testid="privacy-page">
      <header className="flex items-center justify-between px-6 sm:px-12 py-6 border-b border-zinc-200">
        <Link to="/"><Logo size="md" /></Link>
        <LangToggle variant="outline" />
      </header>
      <main className="max-w-3xl mx-auto px-6 sm:px-12 py-12 prose prose-zinc">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">{L.eyebrow}</div>
        <h1 className="mt-2 text-4xl sm:text-5xl font-black tracking-tighter">{L.title}</h1>
        <p className="text-sm text-zinc-500 mt-2">{L.updated}: 06.02.2026</p>

        <Body />

        <div className="mt-12 pt-6 border-t border-zinc-200 text-sm text-zinc-500">
          <Link to="/terms" className="underline hover:text-zinc-900">{L.terms}</Link>
          <span className="mx-2">·</span>
          <Link to="/" className="underline hover:text-zinc-900">{L.home}</Link>
        </div>
      </main>
    </div>
  );
}

const Section = ({ children }) => <div className="text-zinc-700 space-y-6 text-[15px] leading-relaxed mt-8">{children}</div>;
const H = ({ children }) => <h2 className="text-2xl font-bold tracking-tight mt-10">{children}</h2>;
const ContactLine = ({ label }) => (
  <p>{label}: <a href="mailto:privacy@mylumioos.com" className="underline">privacy@mylumioos.com</a></p>
);

const PRIVACY_BODIES = {
  en: () => (
    <Section>
      <p>This Privacy Policy describes how <strong>Lumioos</strong> ("we", "us", "our") collects, uses, and shares information when you use our website <strong>mylumioos.com</strong> and related services (the "Service").</p>
      <H>1. Information We Collect</H>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Account data</strong>: name, email, hashed password.</li>
        <li><strong>Authentication data</strong>: Google email & profile photo if you sign in with Google.</li>
        <li><strong>Usage data</strong>: chat messages, video prompts, interview answers, uploaded resumes/PDFs.</li>
        <li><strong>Payment data</strong>: processed by Stripe — we never see your card details.</li>
        <li><strong>Technical data</strong>: IP address, browser, device type, error logs.</li>
      </ul>
      <H>2. How We Use Your Information</H>
      <p>To operate the Service, manage your subscription, send transactional emails, detect abuse, and comply with legal obligations.</p>
      <H>3. Third-Party Sub-Processors</H>
      <p>OpenAI / Anthropic / Google (AI), Stripe (payments), Resend (emails), MongoDB Atlas (database), Emergent (hosting).</p>
      <H>4. Cookies & Local Storage</H>
      <p>JWT session token + language preference only. No advertising cookies.</p>
      <H>5. Data Retention</H>
      <p>Account data retained while account is active. Deleted within 30 days on request, except legally-required financial records (up to 7 years).</p>
      <H>6. Your Rights (GDPR / KVKK / CCPA)</H>
      <p>Access, correct, delete, export your data, or lodge a complaint with your local data-protection authority. Contact <a href="mailto:privacy@mylumioos.com" className="underline">privacy@mylumioos.com</a>.</p>
      <H>7. Security</H>
      <p>bcrypt password hashing, HTTPS everywhere, signed JWT tokens. We never store card numbers.</p>
      <H>8. Children's Privacy</H>
      <p>Not directed to children under 13 (16 in EU). We don't knowingly collect data from minors.</p>
      <H>9. Changes</H>
      <p>Material changes will be announced via email and on this page.</p>
      <H>10. Contact</H>
      <ContactLine label="For any privacy question" />
    </Section>
  ),
  tr: () => (
    <Section>
      <p>Bu Gizlilik Politikası, <strong>Lumioos</strong>'un ("biz") <strong>mylumioos.com</strong> ve ilgili hizmetleri ("Hizmet") kullanırken bilgilerini nasıl topladığını açıklar.</p>
      <H>1. Topladığımız Bilgiler</H>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Hesap</strong>: ad, e-posta, şifrelenmiş parola.</li>
        <li><strong>Kimlik doğrulama</strong>: Google ile girişte e-posta ve profil fotoğrafın.</li>
        <li><strong>Kullanım</strong>: sohbet, video promptları, mülakat cevapları, yüklediğin CV/PDF.</li>
        <li><strong>Ödeme</strong>: Stripe işler — kart numaranı görmüyoruz.</li>
        <li><strong>Teknik</strong>: IP adresi, tarayıcı, cihaz, hata logları.</li>
      </ul>
      <H>2. Bilgileri Nasıl Kullanıyoruz</H>
      <p>Hizmeti çalıştırmak, aboneliği yönetmek, işlemsel e-postalar göndermek, kötüye kullanımı tespit etmek ve yasal yükümlülüklere uymak için.</p>
      <H>3. Üçüncü Taraf Hizmet Sağlayıcılar</H>
      <p>OpenAI / Anthropic / Google (AI), Stripe (ödeme), Resend (e-posta), MongoDB Atlas (veritabanı), Emergent (barındırma).</p>
      <H>4. Çerezler ve Yerel Depolama</H>
      <p>Sadece JWT oturum token'ı ve dil tercihi. Reklam çerezi yok.</p>
      <H>5. Veri Saklama</H>
      <p>Hesap aktif olduğu sürece saklanır. Talep üzerine 30 gün içinde silinir, yasal mali kayıtlar hariç (7 yıla kadar).</p>
      <H>6. Hakların (KVKK / GDPR / CCPA)</H>
      <p>Verilerine erişme, düzeltme, silme, dışa aktarma hakkın var. <a href="mailto:privacy@mylumioos.com" className="underline">privacy@mylumioos.com</a> adresine yaz.</p>
      <H>7. Güvenlik</H>
      <p>bcrypt ile şifrelenmiş parolalar, HTTPS, imzalı JWT token'ları. Kart numaralarını saklamıyoruz.</p>
      <H>8. Çocukların Gizliliği</H>
      <p>13 yaş (AB'de 16) altına yönelik değildir.</p>
      <H>9. Değişiklikler</H>
      <p>Önemli değişiklikler e-posta ile ve bu sayfada duyurulur.</p>
      <H>10. İletişim</H>
      <ContactLine label="Gizlilikle ilgili her türlü soru için" />
    </Section>
  ),
  es: () => (
    <Section>
      <p>Esta Política de Privacidad describe cómo <strong>Lumioos</strong> recopila, utiliza y comparte información cuando usas <strong>mylumioos.com</strong> y los servicios relacionados (el "Servicio").</p>
      <H>1. Información que Recopilamos</H>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Datos de cuenta</strong>: nombre, email, contraseña cifrada.</li>
        <li><strong>Autenticación</strong>: email y foto de perfil de Google si inicias sesión con Google.</li>
        <li><strong>Uso</strong>: mensajes de chat, prompts de vídeo, respuestas de entrevista, CV/PDF subidos.</li>
        <li><strong>Pago</strong>: procesado por Stripe — nunca vemos los detalles de tu tarjeta.</li>
        <li><strong>Datos técnicos</strong>: IP, navegador, dispositivo, registros de errores.</li>
      </ul>
      <H>2. Cómo Usamos tu Información</H>
      <p>Para operar el Servicio, gestionar tu suscripción, enviar emails transaccionales, detectar abuso y cumplir obligaciones legales.</p>
      <H>3. Subprocesadores Externos</H>
      <p>OpenAI / Anthropic / Google (IA), Stripe (pagos), Resend (emails), MongoDB Atlas (BD), Emergent (hosting).</p>
      <H>4. Cookies y Almacenamiento Local</H>
      <p>Solo token JWT de sesión y preferencia de idioma. Sin cookies publicitarias.</p>
      <H>5. Retención de Datos</H>
      <p>Conservados mientras la cuenta está activa. Eliminados en 30 días bajo petición, excepto registros financieros legalmente requeridos (hasta 7 años).</p>
      <H>6. Tus Derechos (GDPR / CCPA)</H>
      <p>Acceso, rectificación, eliminación, portabilidad. Contacto: <a href="mailto:privacy@mylumioos.com" className="underline">privacy@mylumioos.com</a>.</p>
      <H>7. Seguridad</H>
      <p>Contraseñas con bcrypt, HTTPS, tokens JWT firmados. Nunca almacenamos números de tarjeta.</p>
      <H>8. Privacidad Infantil</H>
      <p>No dirigido a menores de 13 años (16 en la UE).</p>
      <H>9. Cambios</H>
      <p>Los cambios materiales se anunciarán por email y en esta página.</p>
      <H>10. Contacto</H>
      <ContactLine label="Para cualquier consulta de privacidad" />
    </Section>
  ),
  fr: () => (
    <Section>
      <p>Cette Politique de Confidentialité décrit comment <strong>Lumioos</strong> collecte, utilise et partage les informations lorsque tu utilises <strong>mylumioos.com</strong> et les services associés (le "Service").</p>
      <H>1. Informations Collectées</H>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Compte</strong>: nom, email, mot de passe haché.</li>
        <li><strong>Authentification</strong>: email et photo de profil Google si tu te connectes avec Google.</li>
        <li><strong>Utilisation</strong>: messages de chat, prompts vidéo, réponses d'entretien, CV/PDF téléchargés.</li>
        <li><strong>Paiement</strong>: traité par Stripe — nous ne voyons jamais tes informations de carte.</li>
        <li><strong>Données techniques</strong>: IP, navigateur, appareil, logs d'erreur.</li>
      </ul>
      <H>2. Comment nous Utilisons tes Informations</H>
      <p>Pour faire fonctionner le Service, gérer ton abonnement, envoyer des emails transactionnels, détecter les abus et respecter les obligations légales.</p>
      <H>3. Sous-traitants Tiers</H>
      <p>OpenAI / Anthropic / Google (IA), Stripe (paiements), Resend (emails), MongoDB Atlas (BD), Emergent (hébergement).</p>
      <H>4. Cookies et Stockage Local</H>
      <p>Uniquement token JWT de session et préférence de langue. Pas de cookies publicitaires.</p>
      <H>5. Conservation des Données</H>
      <p>Conservées tant que le compte est actif. Supprimées dans les 30 jours sur demande, sauf registres financiers légalement requis (jusqu'à 7 ans).</p>
      <H>6. Tes Droits (RGPD / CCPA)</H>
      <p>Accès, rectification, suppression, portabilité. Contact: <a href="mailto:privacy@mylumioos.com" className="underline">privacy@mylumioos.com</a>.</p>
      <H>7. Sécurité</H>
      <p>Mots de passe hachés avec bcrypt, HTTPS, tokens JWT signés. Aucun numéro de carte stocké.</p>
      <H>8. Confidentialité des Enfants</H>
      <p>Non destiné aux enfants de moins de 13 ans (16 dans l'UE).</p>
      <H>9. Modifications</H>
      <p>Les changements importants seront annoncés par email et sur cette page.</p>
      <H>10. Contact</H>
      <ContactLine label="Pour toute question de confidentialité" />
    </Section>
  ),
  de: () => (
    <Section>
      <p>Diese Datenschutzerklärung beschreibt, wie <strong>Lumioos</strong> Informationen erfasst, verwendet und weitergibt, wenn du <strong>mylumioos.com</strong> und zugehörige Dienste (den "Dienst") nutzt.</p>
      <H>1. Erfasste Informationen</H>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Konto</strong>: Name, E-Mail, gehashtes Passwort.</li>
        <li><strong>Authentifizierung</strong>: Google E-Mail und Profilfoto bei Google-Anmeldung.</li>
        <li><strong>Nutzung</strong>: Chat-Nachrichten, Video-Prompts, Interview-Antworten, hochgeladene Lebensläufe/PDFs.</li>
        <li><strong>Zahlung</strong>: von Stripe verarbeitet — wir sehen nie deine Kartendaten.</li>
        <li><strong>Technische Daten</strong>: IP, Browser, Gerät, Fehlerprotokolle.</li>
      </ul>
      <H>2. Wie wir deine Informationen nutzen</H>
      <p>Zur Bereitstellung des Dienstes, Abonnement-Verwaltung, transaktionalen E-Mails, Missbrauchserkennung und Erfüllung gesetzlicher Pflichten.</p>
      <H>3. Auftragsverarbeiter</H>
      <p>OpenAI / Anthropic / Google (KI), Stripe (Zahlungen), Resend (E-Mails), MongoDB Atlas (Datenbank), Emergent (Hosting).</p>
      <H>4. Cookies und lokale Speicherung</H>
      <p>Nur JWT-Sitzungstoken und Spracheinstellung. Keine Werbe-Cookies.</p>
      <H>5. Datenspeicherung</H>
      <p>Aufbewahrt solange Konto aktiv. Auf Anfrage in 30 Tagen gelöscht, außer gesetzlich erforderliche Finanzdaten (bis zu 7 Jahre).</p>
      <H>6. Deine Rechte (DSGVO / CCPA)</H>
      <p>Zugriff, Berichtigung, Löschung, Portabilität. Kontakt: <a href="mailto:privacy@mylumioos.com" className="underline">privacy@mylumioos.com</a>.</p>
      <H>7. Sicherheit</H>
      <p>bcrypt-Passwort-Hashing, HTTPS überall, signierte JWT-Token. Wir speichern niemals Kartennummern.</p>
      <H>8. Datenschutz für Kinder</H>
      <p>Nicht für Kinder unter 13 (16 in der EU) bestimmt.</p>
      <H>9. Änderungen</H>
      <p>Wesentliche Änderungen werden per E-Mail und auf dieser Seite angekündigt.</p>
      <H>10. Kontakt</H>
      <ContactLine label="Bei Datenschutzfragen" />
    </Section>
  ),
  it: () => (
    <Section>
      <p>Questa Informativa sulla Privacy descrive come <strong>Lumioos</strong> raccoglie, utilizza e condivide le informazioni quando usi <strong>mylumioos.com</strong> e i servizi correlati (il "Servizio").</p>
      <H>1. Informazioni che Raccogliamo</H>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Account</strong>: nome, email, password con hash.</li>
        <li><strong>Autenticazione</strong>: email e foto profilo Google se accedi con Google.</li>
        <li><strong>Utilizzo</strong>: messaggi di chat, prompt video, risposte di colloquio, CV/PDF caricati.</li>
        <li><strong>Pagamento</strong>: elaborato da Stripe — non vediamo mai i dettagli della tua carta.</li>
        <li><strong>Dati tecnici</strong>: IP, browser, dispositivo, log di errore.</li>
      </ul>
      <H>2. Come Usiamo le tue Informazioni</H>
      <p>Per gestire il Servizio, gestire il tuo abbonamento, inviare email transazionali, rilevare abusi e rispettare obblighi legali.</p>
      <H>3. Subprocessori Terzi</H>
      <p>OpenAI / Anthropic / Google (IA), Stripe (pagamenti), Resend (email), MongoDB Atlas (DB), Emergent (hosting).</p>
      <H>4. Cookie e Memoria Locale</H>
      <p>Solo token JWT di sessione e preferenza lingua. Nessun cookie pubblicitario.</p>
      <H>5. Conservazione dei Dati</H>
      <p>Conservati finché l'account è attivo. Eliminati entro 30 giorni su richiesta, eccetto registri finanziari richiesti dalla legge (fino a 7 anni).</p>
      <H>6. I tuoi Diritti (GDPR / CCPA)</H>
      <p>Accesso, rettifica, eliminazione, portabilità. Contatto: <a href="mailto:privacy@mylumioos.com" className="underline">privacy@mylumioos.com</a>.</p>
      <H>7. Sicurezza</H>
      <p>Password con bcrypt, HTTPS, token JWT firmati. Non memorizziamo mai numeri di carta.</p>
      <H>8. Privacy dei Minori</H>
      <p>Non rivolto a minori di 13 anni (16 nell'UE).</p>
      <H>9. Modifiche</H>
      <p>Le modifiche sostanziali saranno annunciate via email e su questa pagina.</p>
      <H>10. Contatto</H>
      <ContactLine label="Per qualsiasi domanda sulla privacy" />
    </Section>
  ),
};
